import { Brand } from "@/core/Brand";
import { Store } from "@/core/Store";
import { use, useRef, useState } from "react";

import { EnvironmentAPI } from "@/api/EnvironmentAPI";
import { Button } from "@/components";
import styles from "@/styles/containers/configure.module.scss";

import classNames from "classnames/bind";
import { ConfigurationFieldInput } from "./ConfigurationFieldInput";
import { ConfigurationFieldLabel } from "./ConfigurationFieldLabel";

import { Alert } from "@/components/Alert";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";

import Image from "next/image";
import { useSaiposTableConfiguration } from "@/hooks/useSaiposTableConfiguration";

const css = classNames.bind(styles);

export default function SaiposConfiguration({ onBack, onConfirm, onOpenQRCode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [fieldToEdit, setFieldToEdit] = useState(null);

  const [appConfigurations, updateAppConfigurations, persistAppConfigurations] =
    useAppConfiguration();

  const [configurations, updateConfigurations, persistConfigurations, isValid] =
    useSaiposTableConfiguration();

  const onOpen = async () => {
    setLoading(true);

    if (isValid) {
      persistConfigurations();
      persistAppConfigurations();

      try {
        await loadEnvironment(appConfigurations.unitCode);
      } catch (err) {
        console.error(err);
      }
    }

    setLoading(false);
  };

  const loadEnvironment = async (code) => {
    try {
      setLoading(true);

      const environmentResponse =
        await EnvironmentAPI.getEnvironmentByCode(code);

      const data = environmentResponse.getData({});
      const brandAlias = data.brandAlias;
      const storeAlias = data.unitAlias;

      if (environmentResponse.getErrors([]).length === 0) {
        await Brand.install(brandAlias);
        await Store.install(storeAlias);

        Brand.alias = brandAlias;
        Store.alias = storeAlias;

        onConfirm();
      } else {
        setError(true);
        setErrorMessage(`${environmentResponse.getErrors([])?.[0]}`);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!fieldToEdit && (
        <>
          <div className={css({ welcome: true, show: true })}>
            <div className={styles.logo}>
              <Image
                src="https://olga.tech/favicon-olga.png"
                alt="Olga"
                width={95}
                height={95}
              />
            </div>

            <p className={styles.title}>Boas vindas</p>

            <p className={styles.version}>
              App {process.env.NEXT_PUBLIC_VERSION}
            </p>

            <Button
              design="primary"
              className={styles.logButton}
              onClick={() => onOpenQRCode()}
            >
              Ler QR Code
            </Button>


            <ConfigurationFieldLabel
              label="Código da unidade"
              value={appConfigurations.unitCode}
              onClick={() => setFieldToEdit("UNIT")}
            />

            <ConfigurationFieldLabel
              label="Código da unidade da Saipos (Cod. Store)"
              value={configurations.codStore}
              onClick={() => setFieldToEdit("COD_STORE")}
            />

            <ConfigurationFieldLabel
              label="Código da unidade da Saipos (ID Partner)"
              value={configurations.idPartner}
              onClick={() => setFieldToEdit("ID_PARTNER")}
            />

            {/* <ConfigurationFieldLabel
              label="SKU do produto (Chamar garçom)"
              value={configurations.callWaiterSku}
              onClick={() => setFieldToEdit("CALL_WAITER_SKU")}
            /> */}

            <ConfigurationFieldLabel
              label="Código da mesa"
              value={configurations.externalId}
              onClick={() => setFieldToEdit("TABLE")}
            />

            <ConfigurationFieldLabel
              label="Exibir proteção infantil antes de enviar o pedido"
              value={
                !!appConfigurations.allowChallenge === true ? "Sim" : "Não"
              }
              onClick={() => {
                updateAppConfigurations({
                  allowChallenge: !appConfigurations.allowChallenge,
                });
              }}
            />
          </div>

          <div className={styles.footer}>
            <Button design="secondary" onClick={() => onBack()}>
              Voltar
            </Button>

            <Button
              onClick={onOpen}
              loading={loading}
              disabled={loading || !(appConfigurations?.unitCode && isValid)}
            >
              Abrir
            </Button>
          </div>
        </>
      )}

      {fieldToEdit === "UNIT" && (
        <ConfigurationFieldInput
          label="Insira o código da unidade"
          value={appConfigurations.unitCode}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateAppConfigurations({
              unitCode: value,
              tableService: "SAIPOS",
            });
          }}
        />
      )}

      {fieldToEdit === "COD_STORE" && (
        <ConfigurationFieldInput
          label="Insira o código da unidade da Saipos (Cod. Store)"
          value={appConfigurations.codStore}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ codStore: value });
          }}
        />
      )}

      {fieldToEdit === "ID_PARTNER" && (
        <ConfigurationFieldInput
          label="Insira o código da unidade da Saipos (ID Partner)"
          value={appConfigurations.idPartner}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ idPartner: value });
          }}
        />
      )}

      {fieldToEdit === "TABLE" && (
        <ConfigurationFieldInput
          label="Insira o código da mesa"
          value={configurations.externalId}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ externalId: value });
          }}
        />
      )}

      {fieldToEdit === "CALL_WAITER_SKU" && (
        <ConfigurationFieldInput
          label="Insira o SKU do produto que será enviado ao chamar o garçom"
          maxLength="8"
          value={configurations.callWaiterSku}
          onValidate={(value) => true}
          onCancel={() => setFieldToEdit(null)}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({
              callWaiterSku: value,
            });
          }}
        />
      )}

      <Alert
        title={errorMessage}
        subtitle="Não foi possível carregar os dados da unidade"
        open={!!error}
        onClose={() => setError(null)}
      />
    </>
  );
}
